import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, ToggleLeft, ToggleRight, Database, MessageSquare, FileText, Clock, Info, Check, X } from 'lucide-react';
import { useAISettings } from '../../../hooks/useAISettings';
import { useAuth } from '../../../contexts/AuthContext';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useMiniApps } from '../../../hooks/useMiniApps';
import { supabase } from '../../../lib/supabase';
import { createFineTuningFile } from '../../../services/fineTuningService';
import { toast } from 'react-hot-toast';

interface DataSourceOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const dataSourceOptions: DataSourceOption[] = [
  {
    id: 'knowledge_base',
    name: 'Knowledge Base',
    description: 'Your existing knowledge base',
    icon: <Database className="w-5 h-5 text-indigo-400" />
  },
  {
    id: 'prompts',
    name: 'Prompts & Objectives',
    description: 'Your custom prompts and objectives',
    icon: <MessageSquare className="w-5 h-5 text-green-400" />
  },
  {
    id: 'conversations',
    name: 'Conversations',
    description: 'All chatbot conversations',
    icon: <MessageSquare className="w-5 h-5 text-blue-400" />
  },
  {
    id: 'lead_conversations',
    name: 'Lead Conversations',
    description: 'Conversations that generated leads',
    icon: <MessageSquare className="w-5 h-5 text-orange-400" />
  },
  {
    id: 'product_content',
    name: 'Product Content',
    description: 'Content from Product Mini App',
    icon: <FileText className="w-5 h-5 text-purple-400" />
  },
  {
    id: 'real_estate',
    name: 'Real Estate Content',
    description: 'Content from Real Estate Mini App',
    icon: <FileText className="w-5 h-5 text-red-400" />
  },
  {
    id: 'services',
    name: 'Services Content',
    description: 'Content from Services Mini App',
    icon: <FileText className="w-5 h-5 text-yellow-400" />
  }
];

export const FineTuningSettings: React.FC = () => {
  const { user } = useAuth();
  const { chatbotPublicId } = useChatbot();
  const { 
    fineTunedModels, 
    startFineTuning,
    activateFineTunedModel,
    knowledgeBase,
    objectives,
    loading: aiSettingsLoading
  } = useAISettings();
  const { miniApps } = useMiniApps();
  
  const [isFineTuningEnabled, setIsFineTuningEnabled] = useState<boolean>(
    !!fineTunedModels?.active
  );
  const [showDisableConfirmation, setShowDisableConfirmation] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [estimatedTime, setEstimatedTime] = useState<string>('~4 hours');
  const [availableSources, setAvailableSources] = useState<Record<string, boolean>>({});
  const [sourceDataCount, setSourceDataCount] = useState<Record<string, number>>({});
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);

  // Reset error message when component changes state
  useEffect(() => {
    setUploadError(null);
  }, [isFineTuningEnabled, selectedSources]);

  // Update local state when fine-tuned models change
  useEffect(() => {
    setIsFineTuningEnabled(!!fineTunedModels?.active);
  }, [fineTunedModels]);

  // Initialize selectedSources with available sources when component loads
  useEffect(() => {
    if (Object.keys(availableSources).length > 0) {
      // Automatically select all available sources by default
      const availableSourceIds = Object.entries(availableSources)
        .filter(([_, isAvailable]) => isAvailable)
        .map(([id, _]) => id);
      
      if (availableSourceIds.length > 0 && selectedSources.length === 0) {
        setSelectedSources(availableSourceIds);
        console.log('Auto-selected available sources:', availableSourceIds);
      }
    }
  }, [availableSources]);

  useEffect(() => {
    // Check availability of each source
    const checkSourceAvailability = async () => {
      if (!chatbotPublicId) return;
      
      setIsCheckingAvailability(true);
      const availability: Record<string, boolean> = {};
      const counts: Record<string, number> = {};
      
      try {
        // Check knowledge base
        availability.knowledge_base = knowledgeBase.length > 0;
        counts.knowledge_base = knowledgeBase.length;
        
        // Check prompts and objectives
        availability.prompts = objectives.length > 0;
        counts.prompts = objectives.length;
        
        // Check conversations
        const { count: conversationsCount, error: conversationsError } = await supabase
          .from('conversations')
          .select('*', { count: 'exact', head: true })
          .eq('chatbot_public_id', chatbotPublicId);
        
        availability.conversations = conversationsCount ? conversationsCount > 0 : false;
        counts.conversations = conversationsCount || 0;
        
        // Check lead conversations
        const { count: leadsCount, error: leadsError } = await supabase
          .from('conversation_contact_info')
          .select('*', { count: 'exact', head: true })
          .eq('chatbot_public_id', chatbotPublicId);
        
        availability.lead_conversations = leadsCount ? leadsCount > 0 : false;
        counts.lead_conversations = leadsCount || 0;
        
        // Check product content (Mini App)
        const productApp = miniApps.find(app => app.id === 'products');
        if (productApp?.enabled) {
          const { count: productsCount, error: productsError } = await supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('chatbot_public_id', chatbotPublicId);
          
          availability.product_content = productsCount ? productsCount > 0 : false;
          counts.product_content = productsCount || 0;
        } else {
          availability.product_content = false;
          counts.product_content = 0;
        }
        
        // Check real estate content (Mini App)
        const realEstateApp = miniApps.find(app => app.id === 'realestate');
        if (realEstateApp?.enabled) {
          const { count: propertiesCount, error: propertiesError } = await supabase
            .from('realestate_properties')
            .select('*', { count: 'exact', head: true })
            .eq('chatbot_public_id', chatbotPublicId);
          
          availability.real_estate = propertiesCount ? propertiesCount > 0 : false;
          counts.real_estate = propertiesCount || 0;
        } else {
          availability.real_estate = false;
          counts.real_estate = 0;
        }
        
        // Check services content (Mini App)
        const servicesApp = miniApps.find(app => app.id === 'services');
        if (servicesApp?.enabled) {
          const { count: servicesCount, error: servicesError } = await supabase
            .from('services')
            .select('*', { count: 'exact', head: true })
            .eq('chatbot_public_id', chatbotPublicId);
          
          availability.services = servicesCount ? servicesCount > 0 : false;
          counts.services = servicesCount || 0;
        } else {
          availability.services = false;
          counts.services = 0;
        }
        
        setAvailableSources(availability);
        setSourceDataCount(counts);
        
        // Update selected sources to keep only available ones
        setSelectedSources(prev => prev.filter(source => availability[source]));
      } catch (error) {
        console.error('Error checking sources:', error);
      } finally {
        setIsCheckingAvailability(false);
      }
    };

    if (chatbotPublicId) {
      checkSourceAvailability();
    }
  }, [chatbotPublicId, knowledgeBase, objectives, miniApps]);

  const handleToggleFineTuning = async () => {
    if (!chatbotPublicId) {
      toast.error('No chatbot ID available');
      setUploadError('No chatbot ID available');
      return;
    }
    
    const newState = !isFineTuningEnabled;
    
    if (!newState && !showDisableConfirmation) {
      setShowDisableConfirmation(true);
      return;
    }
    
    try {
      setIsActivating(true);
      setUploadError(null);
      
      if (newState) {
        const toastId = 'activating-fine-tuning';
        toast.loading('Activating fine-tuning...', { id: toastId });
        console.log('Attempting to activate fine-tuning model: gpt-4.1-mini');
        
        // Check current state of fine-tuned models
        console.log('Current fine-tuned models state:', fineTunedModels);
        
        const success = await activateFineTunedModel('gpt-4.1-mini', chatbotPublicId);
        if (!success) {
          toast.error('Failed to activate fine-tuning', { id: toastId });
          throw new Error('Failed to activate fine-tuned model');
        }
        
        toast.success('Fine-tuning activated successfully', { id: toastId });
        console.log('Fine-tuning model activated successfully');
      } else {
        const toastId = 'disabling-fine-tuning';
        toast.loading('Disabling fine-tuning...', { id: toastId });
        console.log('Attempting to disable fine-tuning model');
        
        const success = await activateFineTunedModel('none', chatbotPublicId);
        if (!success) {
          toast.error('Failed to disable fine-tuning', { id: toastId });
          throw new Error('Failed to disable fine-tuned model');
        }
        
        toast.success('Fine-tuning disabled successfully', { id: toastId });
        console.log('Fine-tuning model disabled successfully');
      }
      
      // Update local state after successful API call
      setIsFineTuningEnabled(newState);
      setShowDisableConfirmation(false);
      setUploadError(null);
    } catch (error) {
      console.error('Error toggling fine-tuning:', error);
      const errorMessage = `Failed to ${newState ? 'enable' : 'disable'} fine-tuning. ${error instanceof Error ? error.message : 'Please try again.'}`;
      setUploadError(errorMessage);
    } finally {
      setIsActivating(false);
    }
  };

  const handleStartFineTuning = async () => {
    if (!chatbotPublicId) return;
    
    // Make sure we have at least one source selected
    if (selectedSources.length === 0) {
      setUploadError('Please select at least one data source.');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadError(null);
      
      const toastId = 'fine-tuning-process';
      toast.loading('Preparing training data...', { id: toastId });
      console.log('Starting fine-tuning with sources:', selectedSources);
      
      // Create training file with selected sources
      const fileResult = await createFineTuningFile(chatbotPublicId, selectedSources);
      
      if (!fileResult.success || !fileResult.fileId) {
        toast.error('Failed to create training file', { id: toastId });
        throw new Error(fileResult.error || 'Failed to create training file');
      }
      
      toast.loading('Created training file, starting fine-tuning...', { id: toastId });
      console.log('Created training file:', fileResult.fileId, 'with data size:', fileResult.dataSize, 'characters');
      
      // Start fine-tuning with created file
      const result = await startFineTuning(chatbotPublicId, {
        fileId: fileResult.fileId,
        model: "gpt-4.1-mini",
        dataSources: selectedSources
      });
      
      if (!result) {
        toast.error('Failed to start fine-tuning process', { id: toastId });
        throw new Error('Failed to start fine-tuning');
      }
      
      toast.success('Fine-tuning started successfully', { id: toastId });
      console.log('Fine-tuning started successfully');
    } catch (error) {
      console.error('Error starting fine-tuning:', error);
      setUploadError(error instanceof Error 
        ? `Failed to start fine-tuning: ${error.message}` 
        : 'Failed to start fine-tuning. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const toggleSource = (sourceId: string) => {
    if (!availableSources[sourceId]) return;
    
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(id => id !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  // Main button is disabled when:
  // 1. Fine-tuning is not enabled
  // 2. No sources are selected
  // 3. System is checking availability
  // 4. System is uploading or activating
  const isMainButtonDisabled = !isFineTuningEnabled || 
                               selectedSources.length === 0 || 
                               isCheckingAvailability || 
                               isUploading || 
                               isActivating ||
                               aiSettingsLoading;

  // Show clear reason why button is disabled
  const getButtonDisabledReason = () => {
    if (!isFineTuningEnabled) return "Fine-tuning must be enabled first";
    if (selectedSources.length === 0) return "Select at least one data source";
    if (isCheckingAvailability) return "Checking data sources availability";
    if (isUploading) return "Processing fine-tuning request";
    if (isActivating) return "Activating fine-tuning";
    if (aiSettingsLoading) return "Loading AI settings";
    return "";
  };

  return (
    <div className="space-y-6">
      {/* Fine-tuning toggle switch */}
      <div className="flex items-center justify-between p-4 border border-white/10 rounded-lg bg-white/5 relative">
        {(isUploading || isCheckingAvailability || isActivating) && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
              <span className="text-xs text-gray-400">
                {isActivating ? 'Activating fine-tuning...' : 
                 isUploading ? 'Processing fine-tuning...' : 
                 'Checking data sources...'}
              </span>
            </div>
          </div>
        )}
        <div>
          <h4 className="font-medium text-white">Enable Fine-Tuning</h4>
          <p className="text-sm text-gray-400 mt-1">
            Use GPT4OMINI to improve your chatbot's responses
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isFineTuningEnabled ? (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
              Fine-tuning active
            </span>
          ) : (
            <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded-full">
              Disabled
            </span>
          )}
          <button
            onClick={handleToggleFineTuning}
            className={`text-indigo-300 hover:text-indigo-200 transition-colors ${(isUploading || isActivating) ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isUploading || isActivating}
          >
            {isFineTuningEnabled ? (
              <ToggleRight className="w-10 h-10" />
            ) : (
              <ToggleLeft className="w-10 h-10" />
            )}
          </button>
        </div>
      </div>

      {/* Disable confirmation */}
      {showDisableConfirmation && (
        <div className="p-4 border border-yellow-500/30 rounded-lg bg-yellow-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-white">Disable Fine-Tuning?</h4>
              <p className="text-sm text-gray-400 mt-1">
                Disabling fine-tuning will remove the model ID and revert to the prompt-based system.
                Are you sure you want to continue?
              </p>
              <div className="flex gap-3 mt-3">
                <button
                  onClick={handleToggleFineTuning}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm"
                >
                  Confirm Disable
                </button>
                <button
                  onClick={() => setShowDisableConfirmation(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data sources */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">Data Sources</h3>
        <p className="text-sm text-gray-400">
          Select data sources to use for fine-tuning
        </p>
        
        <div className="grid gap-4">
          {dataSourceOptions.map(source => {
            const isAvailable = availableSources[source.id];
            const isSelected = selectedSources.includes(source.id);
            const count = sourceDataCount[source.id] || 0;
            
            return (
              <div 
                key={source.id}
                className={`p-4 border rounded-lg transition-all ${
                  isAvailable
                    ? isSelected
                      ? 'border-indigo-500 bg-indigo-500/10 cursor-pointer'
                      : 'border-white/10 hover:border-white/20 cursor-pointer'
                    : 'border-gray-700/50 bg-gray-800/50 cursor-not-allowed'
                }`}
                onClick={() => toggleSource(source.id)}
              >
                <div className="flex items-center gap-3">
                  {source.icon}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-white">{source.name}</h4>
                      {!isAvailable && (
                        <span className="text-xs bg-gray-700/50 text-gray-400 px-2 py-0.5 rounded-full">
                          Not available
                        </span>
                      )}
                      {isAvailable && count > 0 && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                          {count} item{count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{source.description}</p>
                    {!isAvailable && (
                      <p className="text-xs text-gray-500 mt-1">
                        {source.id.includes('mini') || source.id === 'real_estate' || source.id === 'services' || source.id === 'product_content'
                          ? 'This Mini App is not enabled or contains no data.'
                          : 'This source is not yet configured or contains no data.'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fine-tuning vs Prompt comparison */}
      <div className="p-4 border border-white/10 rounded-lg bg-white/5">
        <h3 className="text-lg font-medium text-white mb-3">Fine-tuning vs Standard Prompt</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-indigo-400 mb-2">With Fine-tuning</h4>
            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                More precise and contextual responses
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                Better domain understanding
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                More natural responses
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-400 mb-2">Without Fine-tuning</h4>
            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-400" />
                Responses based on prompts
              </li>
              <li className="flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-400" />
                Requires detailed prompts
              </li>
              <li className="flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-400" />
                More flexible for quick changes
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Wait time */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Clock className="w-4 h-4" />
        <span>Estimated time: {estimatedTime}</span>
      </div>

      {/* Start fine-tuning button with improved error handling */}
      <div className="space-y-2">
        <button
          onClick={handleStartFineTuning}
          disabled={isMainButtonDisabled}
          className={`w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium ${
            isMainButtonDisabled
              ? 'bg-indigo-700/50 text-indigo-300 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Start Fine-Tuning'
          )}
        </button>
        
        {/* Show reason why button is disabled */}
        {isMainButtonDisabled && getButtonDisabledReason() && (
          <p className="text-xs text-center text-gray-500">{getButtonDisabledReason()}</p>
        )}
      </div>

      {/* Show error message with details button if applicable */}
      {uploadError && (
        <div className="flex flex-col gap-2 text-red-400 text-sm p-3 bg-red-500/10 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{uploadError}</span>
          </div>
          
          {/* Common error troubleshooting tips */}
          <div className="mt-2 text-xs">
            <p className="font-medium text-gray-300">Troubleshooting Tips:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-gray-400">
              <li>Make sure you have at least one source with data selected</li>
              <li>Check your network connection</li>
              <li>Try disabling and re-enabling fine-tuning</li>
              <li>If the problem persists, contact support</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Show fine-tuning status if available */}
      {isFineTuningEnabled && fineTunedModels?.status && (
        <div className="p-3 border border-indigo-500/20 bg-indigo-500/5 rounded-lg">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              fineTunedModels.status === 'pending' ? 'bg-yellow-400 animate-pulse' :
              fineTunedModels.status === 'completed' ? 'bg-green-400' :
              fineTunedModels.status === 'failed' ? 'bg-red-400' : 'bg-gray-400'
            }`} />
            <span className="text-sm text-gray-300">
              Status: <span className="text-indigo-300">{fineTunedModels.status}</span>
            </span>
            
            {fineTunedModels.lastTrainingDate && (
              <span className="text-xs text-gray-400 ml-auto">
                Last updated: {new Date(fineTunedModels.lastTrainingDate).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 